// Suburb-specific data for Ipswich-region pest control landing pages.
// Each entry powers a full template page at /pest-control-{slug}.

export const IPSWICH_SUBURBS = {
  brassall: {
    name: "Brassall",
    slug: "brassall",
    postcode: "4305",
    intro1: "Brassall is one of Ipswich's most established residential suburbs, with a mix of post-war timber Queenslanders, brick homes from the 70s and 80s, and newer infill developments. The older housing stock and mature gardens create ideal harbourage for cockroaches, ants, and spiders.",
    intro2: "We service Brassall regularly and understand the local pest pressure points — particularly subfloor cockroach activity in raised timber homes, redback spiders under garden retaining walls, and rodent activity in roof voids.",
    commonIssues: [
      "Subfloor cockroach activity in raised timber Queenslanders",
      "Redback spiders under brick steps and garden retaining walls",
      "Roof void rodent activity, especially in cooler months",
      "Black ant trails entering through weep holes",
      "Spiders nesting in older window frames and eaves",
      "German cockroaches in rental properties at lease change"
    ],
    areaChips: ["Brassall", "North Booval", "Tivoli", "Wulkuraka", "North Tivoli"],
    surroundingNote: "Plus surrounding suburbs including North Ipswich, Tivoli, and Wulkuraka",
  },

  booval: {
    name: "Booval",
    slug: "booval",
    postcode: "4304",
    intro1: "Booval is a busy Ipswich hub with a mix of older character homes, post-war brick housing, commercial premises along Brisbane Road, and rental properties. The density and age of buildings means consistent pest pressure for both residents and businesses.",
    intro2: "We treat homes, units, and businesses across Booval, with particular focus on cockroach management in older brick homes and rodent control near commercial food premises.",
    commonIssues: [
      "Cockroaches in older brick and timber homes",
      "Rodents around commercial premises on Brisbane Road",
      "Ants in kitchens during summer",
      "End-of-lease pest treatments for rentals",
      "Spider activity in garages and sheds",
      "Silverfish in older homes with high humidity"
    ],
    areaChips: ["Booval", "East Ipswich", "Tivoli", "Bundamba", "Newtown"],
    surroundingNote: "Plus surrounding suburbs including East Ipswich, Bundamba, and Newtown",
  },

  bundamba: {
    name: "Bundamba",
    slug: "bundamba",
    postcode: "4304",
    intro1: "Bundamba is a large residential suburb stretching from the Bremer River across to the M2 motorway. With a mix of housing commission, older brick homes, and newer estates, plus the proximity of bushland and the river, Bundamba sees a wide range of pest issues year-round.",
    intro2: "Our technicians treat Bundamba properties regularly — from quick cockroach jobs in rental units to full preventative termite inspections on family homes near the bushland fringe.",
    commonIssues: [
      "Cockroaches in rental properties and units",
      "Termite activity near bushland and river borders",
      "Rodents in older timber homes and sheds",
      "Spider infestations in detached garages",
      "Ants invading kitchens and pantries",
      "Fleas from local wildlife and pets"
    ],
    areaChips: ["Bundamba", "Booval", "Silkstone", "Eastern Heights", "Riverview"],
    surroundingNote: "Plus surrounding suburbs including Booval, Silkstone, and Riverview",
  },

  goodna: {
    name: "Goodna",
    slug: "goodna",
    postcode: "4300",
    intro1: "Goodna sits on the Brisbane–Ipswich corridor with a mix of older established housing, post-flood rebuilds, and newer developments. The proximity to the Brisbane River and local creek systems means moisture-loving pests are a year-round concern.",
    intro2: "We service Goodna homes and businesses with treatments tailored to the local conditions — including subfloor inspections in raised homes, ant management in newer estates, and termite barriers for older properties.",
    commonIssues: [
      "Cockroaches in older homes and post-flood rebuilds",
      "Subterranean termites near creeks and river flats",
      "Ant infestations in newer estate properties",
      "Rodents around commercial strips on Brisbane Road",
      "Spiders in subfloors of raised Queenslanders",
      "Mosquitoes breeding near drainage and creek lines"
    ],
    areaChips: ["Goodna", "Collingwood Park", "Bellbird Park", "Redbank", "Camira"],
    surroundingNote: "Plus surrounding suburbs including Collingwood Park, Bellbird Park, and Redbank",
  },

  "collingwood-park": {
    name: "Collingwood Park",
    slug: "collingwood-park",
    postcode: "4301",
    intro1: "Collingwood Park is a family-friendly residential suburb with predominantly brick-and-tile homes from the 1980s through to newer estates. Surrounded by parkland and bushland reserves, the suburb experiences seasonal spikes in spider, ant, and rodent activity.",
    intro2: "Our team treats Collingwood Park homes regularly with preventative pest plans, end-of-lease treatments, and targeted termite inspections for properties on bushland borders.",
    commonIssues: [
      "Redback spiders in garden walls and outdoor furniture",
      "Black ant trails in summer kitchens",
      "Rodents in roof voids during cooler months",
      "Cockroaches in older brick homes",
      "Termite activity on properties bordering bushland",
      "Spiders in garages and storage sheds"
    ],
    areaChips: ["Collingwood Park", "Bellbird Park", "Goodna", "Carole Park", "Redbank Plains"],
    surroundingNote: "Plus surrounding suburbs including Bellbird Park, Goodna, and Redbank Plains",
  },

  "bellbird-park": {
    name: "Bellbird Park",
    slug: "bellbird-park",
    postcode: "4300",
    intro1: "Bellbird Park is a leafy residential suburb known for its larger blocks, established homes, and proximity to bushland reserves. The mature gardens and nearby bushland mean pest pressure is consistent year-round — particularly for spiders, ants, and termites.",
    intro2: "We provide professional pest management for Bellbird Park homes including subfloor inspections, perimeter treatments, and annual termite checks on properties bordering bushland.",
    commonIssues: [
      "Spiders in mature gardens and outdoor entertaining areas",
      "Termite risk on bushland-border properties",
      "Ants in summer kitchens and pantries",
      "Rodents in larger garden sheds and roof voids",
      "Cockroaches in homes with subfloor moisture",
      "Mosquitoes in shaded backyards"
    ],
    areaChips: ["Bellbird Park", "Collingwood Park", "Goodna", "Camira", "Carole Park"],
    surroundingNote: "Plus surrounding suburbs including Collingwood Park, Goodna, and Camira",
  },

  "north-ipswich": {
    name: "North Ipswich",
    slug: "north-ipswich",
    postcode: "4305",
    intro1: "North Ipswich sits just across the Bremer River from the CBD, with a heritage character of timber Queenslanders, post-war brick homes, and infill apartments. The older housing stock, proximity to the river, and dense suburban layout combine for steady pest pressure.",
    intro2: "We treat homes and businesses across North Ipswich with proven solutions — from cockroach jobs in older brick units to comprehensive treatments for character Queenslanders with subfloor access.",
    commonIssues: [
      "Cockroaches in older brick units and shopfronts",
      "Subfloor spider activity in Queenslanders",
      "Rodents in roof voids of heritage homes",
      "Ants invading via weep holes and skirting gaps",
      "Termite risk near the Bremer River",
      "Silverfish in older homes with humid subfloors"
    ],
    areaChips: ["North Ipswich", "Brassall", "Tivoli", "Wulkuraka", "Coalfalls"],
    surroundingNote: "Plus surrounding suburbs including Brassall, Tivoli, and Coalfalls",
  },

  "east-ipswich": {
    name: "East Ipswich",
    slug: "east-ipswich",
    postcode: "4305",
    intro1: "East Ipswich is a small, established suburb tucked between Booval and the CBD, with timber cottages, post-war brick homes, and a handful of unit complexes. The combination of older housing and dense vegetation creates ideal conditions for cockroaches, spiders, and ants.",
    intro2: "Our East Ipswich treatments focus on the suburb's specific challenges — subfloor cockroach harbourage, redback spiders in older brick retaining walls, and rodent activity in original roof voids.",
    commonIssues: [
      "Cockroach harbourage in raised timber cottages",
      "Redback spiders in older retaining walls",
      "Rodents in original roof voids",
      "Ant trails in summer through weep holes",
      "Silverfish in older homes",
      "End-of-lease treatments for rental properties"
    ],
    areaChips: ["East Ipswich", "Booval", "Ipswich CBD", "Newtown", "Tivoli"],
    surroundingNote: "Plus surrounding suburbs including Booval, Ipswich CBD, and Newtown",
  },

  leichhardt: {
    name: "Leichhardt",
    slug: "leichhardt",
    postcode: "4305",
    intro1: "Leichhardt is a long-established Ipswich residential suburb with a mix of post-war housing, brick homes, and rental properties. The age and density of housing means consistent demand for cockroach control, rodent treatments, and end-of-lease pest jobs.",
    intro2: "We treat Leichhardt properties efficiently with professional-grade products, and offer same-day end-of-lease bond-back treatments for rentals across the suburb.",
    commonIssues: [
      "German cockroaches in rental kitchens",
      "Rodents in older roof voids",
      "Ants entering homes via skirting gaps",
      "Spiders in detached garages and sheds",
      "Fleas in homes with previous pet history",
      "Same-day end-of-lease treatments"
    ],
    areaChips: ["Leichhardt", "One Mile", "West Ipswich", "Wulkuraka", "Yamanto"],
    surroundingNote: "Plus surrounding suburbs including One Mile, West Ipswich, and Yamanto",
  },

  silkstone: {
    name: "Silkstone",
    slug: "silkstone",
    postcode: "4304",
    intro1: "Silkstone is a leafy, established Ipswich suburb with character timber homes, brick post-war housing, and pockets of newer development. The mature trees and older housing stock combine for year-round pest activity — particularly cockroaches, spiders, and seasonal ants.",
    intro2: "Our Silkstone services include preventative 6-monthly treatments, comprehensive packages with roof void dusting, and targeted termite inspections for older homes with timber stumps.",
    commonIssues: [
      "Cockroaches in older brick and timber homes",
      "Spiders in mature gardens and outdoor areas",
      "Ant infestations during summer months",
      "Termite risk on older homes with timber stumps",
      "Rodents in roof voids and sheds",
      "Silverfish in homes with paper storage"
    ],
    areaChips: ["Silkstone", "Eastern Heights", "Booval", "Raceview", "Bundamba"],
    surroundingNote: "Plus surrounding suburbs including Eastern Heights, Booval, and Raceview",
  },

  yamanto: {
    name: "Yamanto",
    slug: "yamanto",
    postcode: "4305",
    intro1: "Yamanto is a growing residential suburb on Ipswich's southern fringe, with a mix of established brick homes and newer estate developments. Its location near bushland and rural blocks means termite pressure and spider activity are higher than average.",
    intro2: "We treat Yamanto homes with comprehensive pest control packages and provide regular termite inspections — particularly important for properties on larger blocks near bushland.",
    commonIssues: [
      "Termite pressure on properties bordering bushland",
      "Redback spiders in newer estate gardens",
      "Cockroaches in established brick homes",
      "Ants entering newer estates via slab penetrations",
      "Rodents from surrounding rural blocks",
      "Mosquitoes in shaded backyards near bushland"
    ],
    areaChips: ["Yamanto", "Deebing Heights", "Flinders View", "Leichhardt", "Amberley"],
    surroundingNote: "Plus surrounding suburbs including Deebing Heights, Flinders View, and Amberley",
  },

  karalee: {
    name: "Karalee",
    slug: "karalee",
    postcode: "4306",
    intro1: "Karalee is a sought-after riverside suburb on Ipswich's north-eastern edge, with larger blocks, established homes, and a strong family demographic. The proximity to the Brisbane River and the suburb's mature trees mean termite and spider activity is significant.",
    intro2: "Our Karalee treatments focus on comprehensive packages — perimeter sprays, roof void dusting, and annual termite inspections to protect homes on bushland and river borders.",
    commonIssues: [
      "Termite activity near the Brisbane River",
      "Spiders in mature gardens and outdoor entertaining areas",
      "Cockroaches in older brick homes",
      "Rodents from surrounding bushland",
      "Ants in summer kitchens",
      "Mosquitoes near creeks and river flats"
    ],
    areaChips: ["Karalee", "Chuwar", "Barellan Point", "Mount Crosby", "Karana Downs"],
    surroundingNote: "Plus surrounding suburbs including Chuwar, Barellan Point, and Karana Downs",
  },

  raceview: {
    name: "Raceview",
    slug: "raceview",
    postcode: "4305",
    intro1: "Raceview is a large, well-established Ipswich suburb known for its brick homes, family streets, and proximity to schools and shopping. The combination of older housing stock and mature gardens means consistent demand for cockroach, ant, and spider control.",
    intro2: "We service Raceview homes with reliable, professional pest control — including preventative 6-monthly plans and comprehensive treatments with roof void dusting.",
    commonIssues: [
      "Cockroaches in older brick homes",
      "Black ants in summer kitchens",
      "Spiders in subfloors and detached garages",
      "Rodents in roof voids during cooler months",
      "Termite risk on properties with timber decks",
      "Silverfish in older homes with paper storage"
    ],
    areaChips: ["Raceview", "Silkstone", "Flinders View", "Yamanto", "Eastern Heights"],
    surroundingNote: "Plus surrounding suburbs including Silkstone, Flinders View, and Eastern Heights",
  },

  woodend: {
    name: "Woodend",
    slug: "woodend",
    postcode: "4305",
    intro1: "Woodend is a heritage Ipswich suburb known for its grand timber Queenslanders, brick character homes, and quiet streets. The age of the housing stock — much of it raised on timber stumps — means subfloor pest activity is a recurring issue.",
    intro2: "Our Woodend treatments are tailored to character homes: full subfloor inspections, perimeter sprays, roof void dusting, and timber-stump termite checks where appropriate.",
    commonIssues: [
      "Subfloor cockroaches in raised Queenslanders",
      "Termite risk on homes with timber stumps",
      "Rodents in heritage roof voids",
      "Silverfish in older homes",
      "Spiders in garden walls and outdoor furniture",
      "Ants invading via cracks in older brickwork"
    ],
    areaChips: ["Woodend", "Sadliers Crossing", "Ipswich CBD", "Coalfalls", "One Mile"],
    surroundingNote: "Plus surrounding suburbs including Sadliers Crossing, Coalfalls, and Ipswich CBD",
  },

  "one-mile": {
    name: "One Mile",
    slug: "one-mile",
    postcode: "4305",
    intro1: "One Mile is an established residential suburb west of the Ipswich CBD, with a mix of post-war brick homes, timber cottages, and rental properties. The older housing density means consistent demand for cockroach control, rodent treatments, and end-of-lease pest jobs.",
    intro2: "We treat One Mile properties with professional-grade products and offer same-day end-of-lease bond-back treatments — accepted by all property managers.",
    commonIssues: [
      "Cockroaches in rental properties",
      "Rodents in older brick homes",
      "End-of-lease pest treatments",
      "Ants entering through weep holes",
      "Spiders in subfloors and garages",
      "Fleas in homes with previous pets"
    ],
    areaChips: ["One Mile", "Leichhardt", "Wulkuraka", "West Ipswich", "Sadliers Crossing"],
    surroundingNote: "Plus surrounding suburbs including Leichhardt, Wulkuraka, and Sadliers Crossing",
  },

  "sadliers-crossing": {
    name: "Sadliers Crossing",
    slug: "sadliers-crossing",
    postcode: "4305",
    intro1: "Sadliers Crossing is a heritage-listed suburb just west of the Ipswich CBD, packed with grand Queenslanders, character cottages, and tree-lined streets. The age and timber construction of most homes means termite and subfloor pest pressure is especially high.",
    intro2: "Our Sadliers Crossing treatments focus on the unique needs of heritage homes — subfloor inspections, comprehensive perimeter sprays, roof void dusting, and annual termite checks on timber-stump properties.",
    commonIssues: [
      "Termite risk on timber-stump heritage homes",
      "Subfloor cockroach activity in raised Queenslanders",
      "Rodents in heritage roof voids",
      "Silverfish in homes with paper and fabric storage",
      "Spiders in subfloors and garden walls",
      "Ants via cracks in older masonry"
    ],
    areaChips: ["Sadliers Crossing", "Woodend", "Coalfalls", "Ipswich CBD", "One Mile"],
    surroundingNote: "Plus surrounding suburbs including Woodend, Coalfalls, and Ipswich CBD",
  },
};

// Map of suburb slugs in /ipswich page → their landing page paths.
// Existing pages are reused; new pages use the /pest-control-{slug} pattern.
export const IPSWICH_SUBURB_LINKS = {
  "Ipswich CBD": "/pest-control-ipswich",
  "Brassall": "/pest-control-brassall",
  "Booval": "/pest-control-booval",
  "Bundamba": "/pest-control-bundamba",
  "Goodna": "/pest-control-goodna",
  "Redbank Plains": "/pest-control-redbank-plains",
  "Collingwood Park": "/pest-control-collingwood-park",
  "Bellbird Park": "/pest-control-bellbird-park",
  "Ripley": "/pest-control-ripley",
  "Springfield": "/pest-control-springfield",
  "North Ipswich": "/pest-control-north-ipswich",
  "East Ipswich": "/pest-control-east-ipswich",
  "Leichhardt": "/pest-control-leichhardt",
  "Silkstone": "/pest-control-silkstone",
  "Yamanto": "/pest-control-yamanto",
  "Karalee": "/pest-control-karalee",
  "Raceview": "/pest-control-raceview",
  "Woodend": "/pest-control-woodend",
  "One Mile": "/pest-control-one-mile",
  "Sadliers Crossing": "/pest-control-sadliers-crossing",
};
